
import PrivateLayout from "@/components/layout/PrivateLayout";
import AvatarSection from "@/lib/profile/AvatarSection";
import ProfileSection from "@/lib/profile/ProfileSection";
import AdvancedInfoSection from "@/lib/profile/AdvancedInfoSection";
import SecuritySection from "@/lib/profile/SecuritySection";
import BankAccountSection from "@/lib/profile/BankAccountSection";
import BillingSection from "@/lib/profile/BillingSection";
import LanguageCurrencySection from "@/lib/profile/LanguageCurrencySection";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { TabsHeader } from "@/components/ui/tabs-header";
import { useEffect, useRef, useState } from "react";
import { User, Settings, CreditCard } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useSidebar } from "@/contexts/SidebarContext";

const UserProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'account-settings';
  const { isSidebarCollapsed } = useSidebar();
  
  // Separate edit state for each tab and section
  const [editStates, setEditStates] = useState({
    'profile': false,
    'account-settings-profile': false,
    'account-settings-security': false,
    'account-settings-language': false,
    'billing-payment-bank': false,
    'billing-payment-billing': false
  });

  // Saving state for billing-payment tab
  const [isSavingBankData, setIsSavingBankData] = useState(false);
  const [isSavingBillingData, setIsSavingBillingData] = useState(false);
  const [isSavingAvatarData, setIsSavingAvatarData] = useState(false);
  
  // Saving state for account-settings sections
  const [isSavingSecurityData, setIsSavingSecurityData] = useState(false);
  
  // Bank data state
  const [bankData, setBankData] = useState({
    iban: "",
    account_holder: "",
    bank_name: "",
    swift_bic: ""
  });

  const handleBankDataChange = (newData: typeof bankData) => {
    setBankData(newData);
  };

  // Billing data state
  const [billingData, setBillingData] = useState({
    billing_address: "",
    is_company: false,
    vat_number: "",
    fiscal_code: ""
  });

  // Avatar data state
  const [avatarData, setAvatarData] = useState({
    avatar_url: "",
    first_name: "",
    last_name: "",
    email: "",
    role: "Designer",
    status: "Attivo"
  });

  // Tabs configuration
  const tabs = [
    {
      value: "profile",
      label: "Profile",
      icon: User,
      disabled: true,
      badge: "Soon",
      badgeVariant: "secondary" as const
    },
    {
      value: "account-settings",
      label: "Account Settings",
      icon: Settings
    },
    {
      value: "billing-payment",
      label: "Billing & Payment",
      icon: CreditCard
    }
  ];

  const handleBillingDataChange = (data: typeof billingData) => {
    setBillingData(data);
  };

  const handleAvatarDataChange = (data: typeof avatarData) => {
    setAvatarData(data);
  };

  const handleEditToggle = (tabId: string) => {
    setEditStates(prev => ({
      ...prev,
      [tabId]: !prev[tabId as keyof typeof prev]
    }));
  };

  const handleCancelEdit = (tabId: string) => {
    setEditStates(prev => ({
      ...prev,
      [tabId]: false
    }));
    // Reset form data to original values if needed
  };

  const handleSaveAvatarData = async () => {
    setIsSavingAvatarData(true);
    try {
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close edit mode after successful save
      setEditStates(prev => ({
        ...prev,
        'profile': false
      }));
    } catch (error) {
      console.error('Error saving avatar data:', error);
    } finally {
      setIsSavingAvatarData(false);
    }
  };

  const handleSaveBankData = async () => {
    setIsSavingBankData(true);
    try {
      // TODO: Implement bank data save logic
      console.log('Saving bank data:', bankData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close edit mode after successful save
      setEditStates(prev => ({
        ...prev,
        'billing-payment-bank': false
      }));
    } catch (error) {
      console.error('Error saving bank data:', error);
    } finally {
      setIsSavingBankData(false);
    }
  };

  const handleSaveBillingData = async () => {
    setIsSavingBillingData(true);
    try {
      // TODO: Implement billing data save logic
      console.log('Saving billing data:', billingData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close edit mode after successful save
      setEditStates(prev => ({
        ...prev,
        'billing-payment-billing': false
      }));
    } catch (error) {
      console.error('Error saving billing data:', error);
    } finally {
      setIsSavingBillingData(false);
    }
  };

  const handleSaveSecurityData = async () => {
    setIsSavingSecurityData(true);
    try {
      // TODO: Implement security data save logic
      console.log('Saving security data');
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close edit mode after successful save
      setEditStates(prev => ({
        ...prev,
        'account-settings-security': false
      }));
    } catch (error) {
      console.error('Error saving security data:', error);
    } finally {
      setIsSavingSecurityData(false);
    }
  };

  const getCurrentEditState = (tabId: string) => {
    return editStates[tabId as keyof typeof editStates] || false;
  };

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <PrivateLayout 
      pageTitle="Profile" 
      pageDescription="Manage your account settings and preferences"
    >
      <div className="space-y-6 mb-12">
        {/* Reusable Tabs Header */}
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />
        
        {/* Tab Content */}
        <div className="max-w-3xl mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <ProfileTabsContent value="profile">
              <div className="space-y-8 flex flex-col gap-12">
                <AvatarSection
                  avatarData={avatarData}
                  onAvatarDataChange={handleAvatarDataChange}
                  isEditing={getCurrentEditState('profile')}
                  onEditToggle={() => handleEditToggle('profile')}
                  onCancelEdit={() => handleCancelEdit('profile')}
                  isSaving={isSavingAvatarData}
                  onSave={handleSaveAvatarData}
                />
                <AdvancedInfoSection 
                  isEditing={getCurrentEditState('profile')}
                />
              </div>
            </ProfileTabsContent>
            
            <ProfileTabsContent value="account-settings">
              <div className="space-y-8 flex flex-col gap-12">
                <ProfileSection 
                  isEditing={getCurrentEditState('account-settings-profile')}
                  onEditToggle={() => handleEditToggle('account-settings-profile')}
                  onCancelEdit={() => handleCancelEdit('account-settings-profile')}
                />
                <SecuritySection 
                  isEditing={getCurrentEditState('account-settings-security')}
                  onEditToggle={() => handleEditToggle('account-settings-security')}
                  onCancelEdit={() => handleCancelEdit('account-settings-security')}
                  isSaving={isSavingSecurityData}
                  onSave={handleSaveSecurityData}
                />
                <LanguageCurrencySection 
                  isEditing={getCurrentEditState('account-settings-language')}
                  onEditToggle={() => handleEditToggle('account-settings-language')}
                  onCancelEdit={() => handleCancelEdit('account-settings-language')}
                />
              </div>
            </ProfileTabsContent>
            
            <ProfileTabsContent value="billing-payment">
              <div className="space-y-8 flex flex-col gap-12">
                <BankAccountSection
                  bankData={bankData}
                  onBankDataChange={handleBankDataChange}
                  isEditing={getCurrentEditState('billing-payment-bank')}
                  onEditToggle={() => handleEditToggle('billing-payment-bank')}
                  onCancelEdit={() => handleCancelEdit('billing-payment-bank')}
                  isSaving={isSavingBankData}
                  onSave={handleSaveBankData}
                />
                <BillingSection
                  billingData={billingData}
                  onBillingDataChange={handleBillingDataChange}
                  isEditing={getCurrentEditState('billing-payment-billing')}
                  onEditToggle={() => handleEditToggle('billing-payment-billing')}
                  onCancelEdit={() => handleCancelEdit('billing-payment-billing')}
                  isSaving={isSavingBillingData}
                  onSave={handleSaveBillingData}
                />
              </div>
            </ProfileTabsContent>
          </Tabs>
        </div>
      </div>
    </PrivateLayout>
  );
};

export default UserProfile;
